import * as React from "react";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

// Número ofuscado para anti-scraping (não exposto como texto puro)
const getWhatsAppUrl = () => {
  const parts = ["351", "928", "360", "852"];
  return `https://wa.me/${parts.join("")}`;
};

interface WhatsAppButtonProps {
  variant?: "icon" | "button" | "text-link" | "floating";
  size?: "sm" | "md" | "lg";
  label?: string;
  className?: string;
  showTooltip?: boolean;
}

const WhatsAppButton: React.FC<WhatsAppButtonProps> = ({
  variant = "button",
  size = "md",
  label = "WhatsApp",
  className,
  showTooltip = true,
}) => {
  const handleClick = () => {
    window.open(getWhatsAppUrl(), "_blank", "noopener,noreferrer");
  };

  const sizeStyles = {
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12",
  };

  const iconSizes = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  // Icon only variant (for header)
  if (variant === "icon") {
    return (
      <motion.button
        onClick={handleClick}
        className={cn(
          "relative inline-flex items-center justify-center rounded-lg transition-colors",
          "text-muted-foreground hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-950/30",
          sizeStyles[size],
          className
        )}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Falar com a ElevAdvisor no WhatsApp"
        title={showTooltip ? "Falar com a ElevAdvisor no WhatsApp" : undefined}
      >
        <MessageCircle className={iconSizes[size]} />
      </motion.button>
    );
  }

  // Text link variant (subtle, for header navigation)
  if (variant === "text-link") {
    return (
      <motion.button
        onClick={handleClick}
        className={cn(
          "inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-green-600 transition-colors",
          className
        )}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        aria-label="Falar com a ElevAdvisor no WhatsApp"
      >
        <MessageCircle className="h-4 w-4" />
        <span>{label}</span>
      </motion.button>
    );
  }

  // Floating button variant (fixed position)
  if (variant === "floating") {
    return (
      <motion.button
        onClick={handleClick}
        className={cn(
          "fixed bottom-6 right-6 z-50 flex items-center justify-center rounded-full shadow-lg",
          "bg-green-600 text-white hover:bg-green-700",
          "h-14 w-14",
          className
        )}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        transition={{ type: "spring", stiffness: 400, damping: 25 }}
        aria-label="Falar com a ElevAdvisor no WhatsApp"
      >
        <MessageCircle className="h-6 w-6" />
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-green-600 animate-ping opacity-20" />
      </motion.button>
    );
  }

  // Default button variant (enterprise style)
  return (
    <Button
      variant="outline"
      size={size === "lg" ? "lg" : size === "sm" ? "sm" : "default"}
      onClick={handleClick}
      className={cn(
        "inline-flex items-center gap-2 border-green-200 text-green-700 hover:bg-green-50 hover:text-green-800 hover:border-green-300",
        "dark:border-green-800 dark:text-green-400 dark:hover:bg-green-950/30 dark:hover:text-green-300",
        className
      )}
      asChild
    >
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        aria-label="Falar com a ElevAdvisor no WhatsApp"
      >
        <MessageCircle className={iconSizes[size]} />
        <span>{label}</span>
      </motion.button>
    </Button>
  );
};

// Enterprise-style CTA for pages (secondary action)
interface WhatsAppCTAProps {
  className?: string;
}

const WhatsAppCTA: React.FC<WhatsAppCTAProps> = ({ className }) => {
  const handleClick = () => {
    window.open(getWhatsAppUrl(), "_blank", "noopener,noreferrer");
  };

  return (
    <motion.div
      className={cn(
        "flex flex-col sm:flex-row items-center justify-center gap-3 p-4 rounded-xl",
        "bg-muted/50 border border-border/50",
        className
      )}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <MessageCircle className="h-4 w-4 text-green-600" />
        <span>Prefere esclarecer dúvidas primeiro?</span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleClick}
        className="text-green-700 hover:text-green-800 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-950/30"
      >
        Falar no WhatsApp
      </Button>
    </motion.div>
  );
};

export { WhatsAppButton, WhatsAppCTA, getWhatsAppUrl };
